import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbUser, TbUsers } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type NotionUsersPayload, parseNotionUsersPayload } from "./notionUsersSchema";

interface NotionUsersRendererProps {
  block: StreamingBlock;
}

// The id column is shown because the write tools embed it as a people-property
// and comment target.
export const NotionUsersRenderer = (props: NotionUsersRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<NotionUsersPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseNotionUsersPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("notion_users_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().users.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbUsers class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("notion_users_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().users.length === 1
                    ? rt.t("notion_users_card.results_one")
                    : rt.t("notion_users_card.results", { count: String(data().users.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().users}>
                  {(user) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <TbUser class="size-3.5 shrink-0 self-center text-muted-text" aria-hidden="true" />
                          <span class="truncate text-sm font-medium text-primary-text">{user.name}</span>
                          <Show when={user.email}>
                            <span class="truncate text-xs text-muted-text">{user.email}</span>
                          </Show>
                        </span>
                        <span class="shrink-0 font-mono text-xs text-muted-text">{user.id}</span>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
